import React, { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helper?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            label,
            error,
            helper,
            leftIcon,
            rightIcon,
            fullWidth = false,
            id,
            disabled,
            ...props
        },
        ref
    ) => {
        const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

        const baseStyles = `
      block w-full rounded-lg border
      px-4 py-2.5
      text-sm text-gray-900 dark:text-gray-100
      placeholder:text-gray-400 dark:placeholder:text-gray-500
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-0
      disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900
    `;

        const stateStyles = error
            ? `
        border-error-500 bg-error-50 dark:bg-error-950
        focus:border-error-500 focus:ring-error-500
      `
            : `
        border-gray-300 dark:border-gray-600
        bg-white dark:bg-gray-800
        hover:border-gray-400 dark:hover:border-gray-500
        focus:border-primary-500 focus:ring-primary-500
      `;

        const iconPadding = leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '';

        return (
            <div className={cn('space-y-1.5', fullWidth ? 'w-full' : '')}>
                {label && (
                    <label
                        htmlFor={inputId}
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {label}
                        {props.required && <span className="text-error-500 ml-1">*</span>}
                    </label>
                )}

                <div className="relative">
                    {leftIcon && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                            {leftIcon}
                        </div>
                    )}

                    <input
                        ref={ref}
                        id={inputId}
                        className={cn(baseStyles, stateStyles, iconPadding, className)}
                        disabled={disabled}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={
                            error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined
                        }
                        {...props}
                    />

                    {rightIcon && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                            {rightIcon}
                        </div>
                    )}
                </div>

                {error && (
                    <p id={`${inputId}-error`} className="text-sm text-error-600 dark:text-error-400">
                        {error}
                    </p>
                )}

                {helper && !error && (
                    <p id={`${inputId}-helper`} className="text-sm text-gray-500 dark:text-gray-400">
                        {helper}
                    </p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
